<template>
  <div class="container-fluid py-4 page-bg">
    <!-- Filters Section -->
    <div class="card shadow-sm border-0 mb-4 filter-card">
      <div class="card-body">
        <div class="row align-items-end">
          <!-- Select Date -->
          <div class="col-md-3 mb-3 mb-md-0">
            <label for="selectDate" class="form-label fw-semibold">Select Date</label>
            <input type="date" id="selectDate" v-model="filters.date" class="form-control" :disabled="isBusy" />
          </div>

          <!-- Select DSR/SE -->
          <div class="col-md-4 mb-3 mb-md-0">
            <label for="selectSalesRep" class="form-label fw-semibold">Select DSR/SE</label>
            <select id="selectSalesRep" v-model="filters.salesRep" class="form-select" :disabled="isBusy || repsLoading">
              <option value="" disabled>Select DSR / SE</option>
              <optgroup v-for="group in groupedSalesReps" :key="group.label" :label="group.label">
                <option v-for="rep in group.options" :key="rep.profile_id ?? rep.id" :value="String(rep.profile_id ?? rep.id)">
                  {{ rep.name }}
                </option>
              </optgroup>
            </select>
          </div>

          <!-- Action Buttons -->
          <div class="col-md-5 d-flex gap-2">
            <button @click="performSearch" class="btn button-default d-flex align-items-center justify-content-center gap-2 flex-grow-1" style="font-weight: normal !important;" :disabled="isBusy">
              <i v-if="isBusy" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-search"></i>
              {{ isBusy ? "Searching..." : "Search" }}
            </button>
            <button @click="exportToPDF" class="btn button-cancel d-flex align-items-center justify-content-center gap-2 flex-grow-1" style="font-weight: normal !important;" :disabled="isBusy || isReportEmpty">
              <i class="fa fa-file-pdf"></i>
              Export to Pdf
            </button>
          </div>
        </div>

        <!-- Show message when data not found for given criteria -->
        <div v-if="!isBusy && isReportEmpty" class="text-center py-5">
          <img src="../../../assets/vectors/data_not_found.png" alt="No Data" class="not-found-image mx-auto d-block" />
          <p class="text-center not-found-message">Results not found!</p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isBusy" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Retrieving cash balancing details...</p>
    </div>

    <div v-else-if="!isReportEmpty">
      <!-- 6 Metrics Cards in a 3x2 Grid -->
      <div class="row mb-4">
        <!-- Total Revenue -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-revenue text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-wallet"></i></div>
            <div class="metric-label">Total Revenue</div>
            <div class="metric-value">Rs. {{ formatNumber(totalRevenue) }}</div>
          </div>
        </div>

        <!-- Total Invoices & Payments -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-invoices text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-file-invoice-dollar"></i></div>
            <div class="metric-label">Total Invoices & Payments</div>
            <div class="metric-value">Rs. {{ formatNumber(totalInvoicesAndPayments) }}</div>
            <div class="">(Invoices: {{ invoicesCount }}, Payments: {{ paymentsCount }})</div>
          </div>
        </div>

        <!-- Total Due Collections -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-due text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-hand-holding-usd"></i></div>
            <div class="metric-label">Total Due Collections</div>
            <div class="metric-value">Rs. {{ formatNumber(totalDueCollections) }}</div>
            <div class="">(Due Collections: {{ duePaymentsCount }})</div>
          </div>
        </div>

        <!-- Total Advance Payments -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-advance text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-money-bill-wave"></i></div>
            <div class="metric-label">Total Advance Payments</div>
            <div class="metric-value">Rs. {{ formatNumber(totalAdvancePayments) }}</div>
            <div class="">(Advance Payments: {{ advancePaymentCount }})</div>
          </div>
        </div>

        <!-- Total Non Productive Calls -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-nonproductive text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-store-slash"></i></div>
            <div class="metric-label">Total Non Productive Calls</div>
            <div class="metric-value">{{ nonProductiveCallsCount }}</div>
          </div>
        </div>

        <!-- Total Stock Returns -->
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="metric-card bg-grad-returns text-white shadow-sm h-100 p-3 rounded-3 position-relative overflow-hidden">
            <div class="metric-icon"><i class="fa fa-boxes"></i></div>
            <div class="metric-label">Total Stock Returns</div>
            <div class="metric-value">{{ stockReturnsCount }}</div>
          </div>
        </div>
      </div>

      <!-- Two-Column Section: Payments by Method & Denominations (Equal height) -->
      <div class="row align-items-stretch mb-4">
        <!-- Payments by Method Card -->
        <div class="col-lg-5 mb-4 mb-lg-0">
          <div class="card shadow-sm border-0 h-100 d-flex flex-column">
            <div class="card-header bg-white border-0 py-3 d-flex align-items-center gap-2">
              <div class="icon-circle bg-primary-light text-primary"><i class="fa fa-credit-card"></i></div>
              <h5 class="card-title m-0 fw-bold">Payments by Method</h5>
            </div>
            <div class="card-body p-3 flex-grow-1 d-flex flex-column gap-2 justify-content-between">
              <div v-for="method in paymentMethods" :key="method.key" class="payment-method-card p-2 px-3 rounded-3 d-flex align-items-center gap-3">
                <div class="method-icon-wrapper rounded-circle d-flex align-items-center justify-content-center" :style="{ backgroundColor: method.bgColor, color: method.iconColor, width: '38px', height: '38px', minWidth: '38px', fontSize: '1.15rem' }">
                  <i :class="['fa', method.icon]"></i>
                </div>
                <div class="method-details d-flex flex-column">
                  <span class="method-name text-muted fw-semibold text-uppercase text-xs" style="letter-spacing: 0.5px; font-size: 0.72rem;">{{ method.name }}</span>
                  <span class="method-amount fw-bold text-dark" style="font-size: 1.05rem;">LKR {{ formatNumber(method.value) }}</span>
                </div>
              </div>
            </div>
            <!-- Summary Footer outside card-body, full sized -->
            <div class="card-footer bg-light border-0 py-3 d-flex justify-content-between align-items-center rounded-bottom border-top">
              <span class="fw-bold text-secondary">Total Payments Collected:</span>
              <span class="fw-bold text-primary fs-5">LKR {{ formatNumber(computedTotalPayments) }}</span>
            </div>
          </div>
        </div>

        <!-- Cash Denominations Counts Card -->
        <div class="col-lg-7">
          <div class="card shadow-sm border-0 h-100 d-flex flex-column">
            <div class="card-header bg-white border-0 py-3 d-flex align-items-center">
              <div class="icon-circle bg-success-light text-success"><i class="fa fa-coins"></i></div>
              <h5 class="card-title m-0 fw-bold">Cash Denominations Counts</h5>
            </div>
            <div class="card-body p-0 flex-grow-1">
              <div class="table-responsive">
                <table class="table table-hover align-middle m-0 border-0">
                  <thead class="table-light">
                    <tr>
                      <th class="ps-4 border-0">Denomination</th>
                      <th class="text-center border-0">Count</th>
                      <th class="text-end pe-4 border-0">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="denom in cashDenominations" :key="denom.denomination">
                      <td class="ps-4 fw-semibold text-secondary">
                        <span v-if="denom.denomination === 'coins'">Coins</span>
                        <span v-else>LKR {{ denom.denomination }}</span>
                      </td>
                      <td class="text-center fw-bold text-dark">{{ denom.count }}</td>
                      <td class="text-end pe-4 fw-bold text-success">LKR {{ formatNumber(denom.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- Denominations Summary Footer outside card-body, full sized -->
            <div class="card-footer bg-light border-0 py-3 d-flex justify-content-between align-items-center rounded-bottom border-top">
              <span class="fw-bold text-secondary">Total Cash Denominated:</span>
              <div>
                <span class="text-muted me-3">Count: <strong class="text-dark">{{ totalDenominationsCount }}</strong></span>
                <span class="fw-bold text-success fs-5">LKR {{ formatNumber(totalDenominationsAmount) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Remarks / Comments Container (Common for both) -->
      <div class="row">
        <div class="col-12">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-0 py-3 d-flex align-items-center gap-2">
              <div class="icon-circle bg-warning-light text-warning"><i class="fa fa-comment-dots"></i></div>
              <h5 class="card-title m-0 fw-bold">Remarks / Comments</h5>
            </div>
            <div class="card-body pt-0 pb-4">
              <textarea v-model="remark" class="form-control bg-light border-0 remark-textarea" rows="3" readonly placeholder="No remarks available..."></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { fetchCashBalancingUsers, fetchCashBalancingReport } from "@/services/dataService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "CashBalancingReportData",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: localStorage.getItem("token_set") || "",
      profileId: Number(localStorage.getItem("profile_id") || 0),

      filters: {
        date: currentDate,
        salesRep: ""
      },

      salesRepList: [],
      repsLoading: false,
      isBusy: false,

      // Metrics variables defined as requested
      totalRevenue: 0.00,
      totalInvoicesAndPayments: 0.00,
      totalDueCollections: 0.00,
      totalAdvancePayments: 0.00,

      // Counts of activities variables
      invoicesCount: 0,
      paymentsCount: 0,
      duePaymentsCount: 0,
      advancePaymentCount: 0,
      nonProductiveCallsCount: 0,
      stockReturnsCount: 0,

      // Payments by Method variables
      totalCashPayments: 0.00,
      totalChequePayments: 0.00,
      totalBankTransfers: 0.00,
      totalAdvancePaymentsAmount: 0.00,
      totalCardPayments: 0.00,
      totalMobilePayments: 0.00,
      totalOnlinePayments: 0.00,

      // Cash denominations count array
      cashDenominations: [
        { denomination: 5000, count: 0, total_amount: 0 },
        { denomination: 2000, count: 0, total_amount: 0 },
        { denomination: 1000, count: 0, total_amount: 0 },
        { denomination: 500, count: 0, total_amount: 0 },
        { denomination: 100, count: 0, total_amount: 0 },
        { denomination: 50, count: 0, total_amount: 0 },
        { denomination: 20, count: 0, total_amount: 0 },
        { denomination: 10, count: 0, total_amount: 0 },
        { denomination: "coins", count: 0, total_amount: 0 }
      ],

      remark: ""
    };
  },
  computed: {
    paymentMethods() {
      return [
        { key: "cash", name: "Cash", icon: "fa-sack-dollar", iconColor: "#069E27", bgColor: "rgba(6, 158, 39, 0.1)", value: this.totalCashPayments },
        { key: "cheque", name: "Cheque", icon: "fa-money-check", iconColor: "#F57C00", bgColor: "rgba(245, 124, 0, 0.1)", value: this.totalChequePayments },
        { key: "bank", name: "Bank Transfer", icon: "fa-money-bill-transfer", iconColor: "#1B6CB4", bgColor: "rgba(27, 108, 180, 0.1)", value: this.totalBankTransfers },
        { key: "advance", name: "Advance", icon: "fa-clock-rotate-left", iconColor: "#FFAB40", bgColor: "rgba(255, 171, 64, 0.1)", value: this.totalAdvancePaymentsAmount },
        { key: "card", name: "Credit/Debit Card", icon: "fa-credit-card", iconColor: "#FF0000", bgColor: "rgba(255, 0, 0, 0.1)", value: this.totalCardPayments },
        { key: "mobile", name: "Mobile Payments", icon: "fa-mobile-screen-button", iconColor: "#A834A3", bgColor: "rgba(168, 52, 163, 0.1)", value: this.totalMobilePayments },
        { key: "online", name: "Online Payments", icon: "fa-earth-asia", iconColor: "#B4CAF8", bgColor: "rgba(180, 202, 248, 0.2)", value: this.totalOnlinePayments }
      ];
    },
    computedTotalPayments() {
      return (
        Number(this.totalCashPayments) +
        Number(this.totalChequePayments) +
        Number(this.totalBankTransfers) +
        Number(this.totalAdvancePaymentsAmount) +
        Number(this.totalCardPayments) +
        Number(this.totalMobilePayments) +
        Number(this.totalOnlinePayments)
      );
    },
    totalDenominationsCount() {
      return this.cashDenominations.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    },
    totalDenominationsAmount() {
      return this.cashDenominations.reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0);
    },
    isReportEmpty() {
      return (
        Number(this.totalRevenue) === 0 &&
        Number(this.totalInvoicesAndPayments) === 0 &&
        Number(this.totalDueCollections) === 0 &&
        Number(this.totalAdvancePayments) === 0 &&
        Number(this.stockReturnsCount) === 0 &&
        Number(this.nonProductiveCallsCount) === 0 &&
        Number(this.computedTotalPayments) === 0 &&
        Number(this.totalDenominationsAmount) === 0 &&
        !this.remark
      );
    },
    groupedSalesReps() {
      const groups = {};
      this.salesRepList.forEach(rep => {
        const type = rep.type || "OTHER";
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(rep);
      });

      // Sort types so 'SALES REP' is first, 'SALES EXECUTIVE' is second, then others
      const orderedTypes = Object.keys(groups).sort((a, b) => {
        const typeA = a.toUpperCase();
        const typeB = b.toUpperCase();
        
        // Define priority
        const getPriority = (type) => {
          if (type.includes("REP")) return 1;
          if (type.includes("EXECUTIVE")) return 2;
          return 3;
        };

        const prioA = getPriority(typeA);
        const prioB = getPriority(typeB);

        if (prioA !== prioB) {
          return prioA - prioB;
        }
        return typeA.localeCompare(typeB);
      });

      return orderedTypes.map(type => ({
        label: type,
        options: groups[type]
      }));
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      if (!this.token) {
        this.toast.error("Session expired. Please log in again.");
        return;
      }
      await this.loadSalesReps();
    },

    async loadSalesReps() {
      this.repsLoading = true;
      try {
        const res = await fetchCashBalancingUsers(this.profileId, this.token);
        const body = res?.data ?? res;
        const listRaw = body?.data || body || [];
        const reps = Array.isArray(listRaw) ? listRaw : [];
        
        // Group/sort by type first so same role users are together, then by name alphabetically
        reps.sort((a, b) => {
          const typeA = String(a.type || "").toLowerCase();
          const typeB = String(b.type || "").toLowerCase();
          if (typeA !== typeB) {
            return typeA.localeCompare(typeB);
          }
          const nameA = String(a.name || "").toLowerCase();
          const nameB = String(b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

        this.salesRepList = reps;
      } catch (err) {
        console.error("Failed to load DSR/SE list:", err);
        this.toast.error("Failed to load DSR/SE list.");
      } finally {
        this.repsLoading = false;
      }
    },

    async performSearch() {
      if (!this.filters.salesRep) {
        this.toast.warning("Please select a DSR/SE first.");
        return;
      }
      this.isBusy = true;
      try {
        const res = await fetchCashBalancingReport(this.token, this.filters.date, this.filters.salesRep);
        const body = res?.data ?? res;
        if (body?.status || body?.status === "success" || body?.status === true) {
          const resData = body?.data;
          if (resData) {
            this.totalRevenue = Number(resData.total_revenue) || 0.00;
            this.totalInvoicesAndPayments = Number(resData.total_invoice_payments) || 0.00;
            this.totalDueCollections = Number(resData.total_due_payments) || 0.00;
            this.totalAdvancePayments = Number(resData.total_advance_payments) || 0.00;

            this.invoicesCount = Number(resData.invoices_count) || 0;
            this.paymentsCount = Number(resData.payments_count) || 0;
            this.duePaymentsCount = Number(resData.due_payments_count) || 0;
            this.advancePaymentCount = Number(resData.advance_payments_count) || 0;
            this.nonProductiveCallsCount = Number(resData.total_npc_count) || 0;
            this.stockReturnsCount = Number(resData.total_stock_returns) || 0;

            this.totalCashPayments = Number(resData.cash_payment_sum) || 0.00;
            this.totalChequePayments = Number(resData.cheque_payment_sum) || 0.00;
            this.totalBankTransfers = Number(resData.bank_transfer_sum) || 0.00;
            this.totalAdvancePaymentsAmount = Number(resData.advance_payment_sum) || 0.00;
            this.totalCardPayments = Number(resData.card_payment_sum) || 0.00;
            this.totalMobilePayments = Number(resData.mobile_payment_sum) || 0.00;
            this.totalOnlinePayments = Number(resData.online_payment_sum) || 0.00;

            this.remark = resData.remark || "";

            // Map cash counters
            if (resData.cash_counters && Array.isArray(resData.cash_counters)) {
              this.cashDenominations = this.cashDenominations.map(localDenom => {
                const matched = resData.cash_counters.find(c => {
                  const apiDenomStr = String(c.denomination).trim().toLowerCase();
                  const localDenomStr = String(localDenom.denomination).trim().toLowerCase();
                  return apiDenomStr === localDenomStr;
                });
                if (matched) {
                  return {
                    denomination: localDenom.denomination,
                    count: Number(matched.count) || 0,
                    total_amount: Number(matched.total_amount) || 0
                  };
                }
                return {
                  ...localDenom,
                  count: 0,
                  total_amount: 0
                };
              });
            } else {
              this.cashDenominations = this.cashDenominations.map(d => ({ ...d, count: 0, total_amount: 0 }));
            }
            this.toast.success(body.message || "Cash balancing report loaded successfully.");
          } else {
            this.toast.warning("No data found for the selected filter.");
            this.resetReportData();
          }
        } else {
          this.toast.warning(body?.message || "Failed to retrieve day end details.");
          this.resetReportData();
        }
      } catch (err) {
        console.error("Search failed:", err);
        this.toast.error("Failed to load report data.");
        this.resetReportData();
      } finally {
        this.isBusy = false;
      }
    },

    resetReportData() {
      this.totalRevenue = 0.00;
      this.totalInvoicesAndPayments = 0.00;
      this.totalDueCollections = 0.00;
      this.totalAdvancePayments = 0.00;

      this.invoicesCount = 0;
      this.paymentsCount = 0;
      this.duePaymentsCount = 0;
      this.advancePaymentCount = 0;
      this.nonProductiveCallsCount = 0;
      this.stockReturnsCount = 0;

      this.totalCashPayments = 0.00;
      this.totalChequePayments = 0.00;
      this.totalBankTransfers = 0.00;
      this.totalAdvancePaymentsAmount = 0.00;
      this.totalCardPayments = 0.00;
      this.totalMobilePayments = 0.00;
      this.totalOnlinePayments = 0.00;

      this.remark = "";
      this.cashDenominations = this.cashDenominations.map(d => ({ ...d, count: 0, total_amount: 0 }));
    },

    exportToPDF() {
      if (!this.filters.salesRep) {
        this.toast.warning("Please select a DSR/SE first.");
        return;
      }

      const generatePdfWithLogo = (logoBase64) => {
        try {
          const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
          });

          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          const marginLeft = 15;

          // Header Logo
          if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', marginLeft, 10, 28, 9);
          }

          // Title
          doc.setFontSize(15);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(37, 48, 105); // Standard deep blue color
          doc.text("CASH BALANCING REPORT", pageWidth - marginLeft, 17, { align: "right" });

          // Divider Line
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.3);
          doc.line(marginLeft, 22, pageWidth - marginLeft, 22);

          // Metadata Info
          const now = new Date();
          const generatedBy = localStorage.getItem("full_name") || "System User";
          const generatedAt = now.toLocaleString();

          let selectedRepName = "";
          const matched = this.salesRepList.find(r => String(r.profile_id ?? r.id) === String(this.filters.salesRep));
          if (matched) {
            selectedRepName = matched.name || `Rep #${this.filters.salesRep}`;
          }

          doc.setFontSize(8.5);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80, 80, 80);

          // Row 1 of Metadata
          doc.text(`DSR / SE: ${selectedRepName}`, marginLeft, 28);
          doc.text(`Report Date: ${this.filters.date}`, pageWidth - marginLeft, 28, { align: "right" });

          // Row 2 of Metadata
          doc.text(`Generated by: ${generatedBy}`, marginLeft, 33);
          doc.text(`GeneratedAt: ${generatedAt}`, pageWidth - marginLeft, 33, { align: "right" });

          // Summary Metrics Cards (3x2 Grid)
          const cardWidth = 56;
          const cardHeight = 22;
          const cardGapX = 6;
          const cardGapY = 4;
          const rx = 1.5; // Corner radius
          const ry = 1.5;

          const cards = [
            {
              label: "TOTAL REVENUE",
              value: `Rs. ${this.formatNumber(this.totalRevenue)}`,
              data: null,
              color: [29, 78, 216] // Blue (#1d4ed8)
            },
            {
              label: "TOTAL INVOICES & PAYMENTS",
              value: `Rs. ${this.formatNumber(this.totalInvoicesAndPayments)}`,
              data: `Invoices: ${this.invoicesCount}, Payments: ${this.paymentsCount}`,
              color: [4, 120, 87] // Green (#047857)
            },
            {
              label: "TOTAL DUE COLLECTIONS",
              value: `Rs. ${this.formatNumber(this.totalDueCollections)}`,
              data: `Due Collections: ${this.duePaymentsCount}`,
              color: [217, 119, 6] // Orange (#d97706)
            },
            {
              label: "TOTAL ADVANCE PAYMENTS",
              value: `Rs. ${this.formatNumber(this.totalAdvancePayments)}`,
              data: `Advance Payments: ${this.advancePaymentCount}`,
              color: [109, 40, 217] // Purple (#6d28d9)
            },
            {
              label: "TOTAL NON PRODUCTIVE CALLS",
              value: String(this.nonProductiveCallsCount),
              data: null,
              color: [185, 28, 28] // Red (#b91c1c)
            },
            {
              label: "TOTAL STOCK RETURNS",
              value: String(this.stockReturnsCount),
              data: null,
              color: [190, 24, 93] // Pink (#be185d)
            },
          ];

          // Draw the 3x2 grid of cards
          cards.forEach((card, idx) => {
            const col = idx % 3;
            const row = Math.floor(idx / 3);
            const x = marginLeft + col * (cardWidth + cardGapX);
            const y = 38 + row * (cardHeight + cardGapY);

            // Draw Card Background
            doc.setFillColor(card.color[0], card.color[1], card.color[2]);
            doc.roundedRect(x, y, cardWidth, cardHeight, rx, ry, "F");

            // Draw Label
            doc.setFontSize(6.5);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(255, 255, 255);
            if (card.data) {
              doc.text(card.label, x + 4, y + 5);
            } else {
              doc.text(card.label, x + 4, y + 7.5);
            }

            // Draw Value
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(255, 255, 255);
            if (card.data) {
              doc.text(card.value, x + 4, y + 12);
            } else {
              doc.text(card.value, x + 4, y + 15);
            }

            // Draw Data
            if (card.data) {
              doc.setFontSize(7);
              doc.setFont("helvetica", "normal");
              doc.setTextColor(255, 255, 255);
              doc.text(card.data, x + 4, y + 18);
            }
          });

          let currentY = 90;

          // Table 2: Payments by Method
          autoTable(doc, {
            startY: currentY,
            margin: { left: 15 },
            tableWidth: 75,
            head: [["Payments by Method", "Amount"]],
            body: [
              ["Cash", `LKR ${this.formatNumber(this.totalCashPayments)}`],
              ["Cheque", `LKR ${this.formatNumber(this.totalChequePayments)}`],
              ["Bank Transfer", `LKR ${this.formatNumber(this.totalBankTransfers)}`],
              ["Advance", `LKR ${this.formatNumber(this.totalAdvancePaymentsAmount)}`],
              ["Credit/Debit Card", `LKR ${this.formatNumber(this.totalCardPayments)}`],
              ["Mobile Payments", `LKR ${this.formatNumber(this.totalMobilePayments)}`],
              ["Online Payments", `LKR ${this.formatNumber(this.totalOnlinePayments)}`],
              ["Total Payments Collected", `LKR ${this.formatNumber(this.computedTotalPayments)}`]
            ],
            theme: "striped",
            headStyles: { fillColor: [46, 125, 50], textColor: [255, 255, 255] },
            styles: { fontSize: 8.5, cellPadding: 2 },
            columnStyles: {
              1: { fontStyle: "bold", halign: "right" }
            }
          });

          const leftTableY = doc.lastAutoTable.finalY;

          // Table 3: Cash Denominations
          const denomRows = this.cashDenominations.map(d => [
            d.denomination === "coins" ? "Coins" : `LKR ${d.denomination}`,
            String(d.count),
            `LKR ${this.formatNumber(d.total_amount)}`
          ]);
          denomRows.push(["Total Cash Denominated", String(this.totalDenominationsCount), `LKR ${this.formatNumber(this.totalDenominationsAmount)}`]);

          autoTable(doc, {
            startY: currentY,
            margin: { left: 100 },
            tableWidth: 95,
            head: [["Denomination", "Count", "Total Amount"]],
            body: denomRows,
            theme: "striped",
            headStyles: { fillColor: [198, 40, 40], textColor: [255, 255, 255] },
            styles: { fontSize: 8.5, cellPadding: 2 },
            columnStyles: {
              1: { halign: "center" },
              2: { fontStyle: "bold", halign: "right" }
            }
          });

          const rightTableY = doc.lastAutoTable.finalY;

          currentY = Math.max(leftTableY, rightTableY) + 8;

          // Remarks Section
          if (currentY + 25 > pageHeight) {
            doc.addPage();
            currentY = 20;
          }

          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Remarks / Comments:", marginLeft, currentY);

          doc.setFont("helvetica", "normal");
          doc.setTextColor(50, 50, 50);
          const remarkLines = doc.splitTextToSize(this.remark || "No remarks available", pageWidth - (marginLeft * 2));
          doc.text(remarkLines, marginLeft, currentY + 6);

          // Save doc
          doc.save(`Cash_Balancing_Report_${this.filters.date}.pdf`);
          this.toast.success("PDF exported successfully.");
        } catch (err) {
          console.error("PDF export failed:", err);
          this.toast.error("Failed to export report to PDF.");
        }
      };

      try {
        const logoImg = new Image();
        logoImg.src = require("@/assets/images/logo/sfa_logo.svg");

        logoImg.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            // Set canvas size (keep aspect ratio of logo)
            canvas.width = logoImg.width || 120;
            canvas.height = logoImg.height || 40;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
            const logoBase64 = canvas.toDataURL("image/png");
            generatePdfWithLogo(logoBase64);
          } catch (e) {
            console.error("Failed to draw logo to canvas, fallback to no-logo PDF", e);
            generatePdfWithLogo(null);
          }
        };

        logoImg.onerror = () => {
          console.warn("sfa_logo.svg failed to load, generating PDF without logo.");
          generatePdfWithLogo(null);
        };
      } catch (err) {
        console.error("Logo loading error:", err);
        generatePdfWithLogo(null);
      }
    },

    formatNumber(v) {
      if (v === undefined || v === null || v === "") return "0.00";
      const n = Number(v);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
.page-bg {
  background-color: #f8fafc;
  min-height: 100vh;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.sub-title {
  font-size: 0.95rem;
  margin-top: 4px;
}

.filter-card {
  border-radius: 12px;
  background-color: #ffffff;
}

.form-label {
  font-size: 0.9rem;
  color: #475569;
}

.form-control, .form-select {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.95rem;
  color: #1e293b;
}

.form-control:focus, .form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.input-group-text {
  border: 1px solid #cbd5e1;
  color: #64748b;
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}

.btn-success {
  background-color: #10b981;
  border-color: #10b981;
}

.btn-success:hover {
  background-color: #059669;
  border-color: #059669;
}

/* Metric Cards */
.metric-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  height: 110px !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.12) !important;
}

.metric-icon {
  font-size: 3.5rem;
  position: absolute;
  right: 15px;
  bottom: -15px;
  opacity: 0.15;
}

.metric-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  opacity: 0.9;
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 700;
  margin-top: 6px;
}

/* Gradients */
.bg-grad-revenue {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
}

.bg-grad-invoices {
  background: linear-gradient(135deg, #10b981, #047857);
}

.bg-grad-due {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.bg-grad-advance {
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
}

.bg-grad-returns {
  background: linear-gradient(135deg, #ec4899, #be185d);
}

.bg-grad-nonproductive {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
}

/* Icon circles for headers */
.icon-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.bg-primary-light {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.bg-success-light {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.bg-warning-light {
  background-color: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.text-purple {
  color: #8b5cf6;
}

.text-indigo {
  color: #6366f1;
}

/* List group styles */
.list-group-item {
  color: #475569;
  font-size: 0.95rem;
}

.list-group-item i {
  width: 20px;
}

/* Table styles */
.table th {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.table td {
  font-size: 0.95rem;
}

.table-responsive {
  overflow: hidden;
}

/* Remark textarea */
.remark-textarea {
  resize: none;
  border-radius: 8px;
  background-color: #f8fafc !important;
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 12px;
  font-size: 0.95rem;
}

.card {
  border-radius: 12px;
}

/* Payment Method Cards Style */
.payment-method-card {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  background-color: #fcfcfc;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.payment-method-card:hover {
  transform: translateX(4px);
  background-color: #ffffff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  border-color: rgba(37, 48, 105, 0.15) !important;
}

/* Results not found image */
.not-found-image {
  max-width: 250px;
}

/* Results not found message */
.not-found-message {
  font-size: 25px;
  font-weight: bold;
  color: #ccc;
  margin-top: -25px;
}
</style>